import React from 'react';
import { Card, CardBody } from '@material-tailwind/react';
import { Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import LoadingPanel from './LoadingPanel';

const MonthlyInvoiceStatusReport = ({
  summaryLoading,
  statusRows,
  paymentRows,
  statusChart,
  paymentChart,
  statusColors,
  paymentColors,
  statusBadgeClass,
  formatMoney
}) => {
  if (summaryLoading) {
    return <LoadingPanel label="Loading status summary..." />;
  }

  return (
    <div className="grid gap-4 xl:grid-cols-2">
      <Card className="shadow-lg">
        <CardBody className="p-0">
          <div className="border-b border-slate-200 px-5 py-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Status breakdown</div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Status</th>
                  <th className="px-4 py-3 text-right font-semibold">Count</th>
                  <th className="px-4 py-3 text-right font-semibold">Gross</th>
                  <th className="px-4 py-3 text-right font-semibold">Discount</th>
                  <th className="px-4 py-3 text-right font-semibold">Net</th>
                </tr>
              </thead>
              <tbody>
                {statusRows.map((row) => (
                  <tr key={row.status} className="border-t border-slate-200">
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${statusBadgeClass(row.status)}`}>{row.status}</span>
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-900">{Number(row.count || 0)}</td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-900">{formatMoney(row.gross_amount || 0)}</td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-900">{formatMoney(row.discount_amount || 0)}</td>
                    <td className="px-4 py-3 text-right font-semibold text-emerald-700">{formatMoney(row.net_amount || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>

      <Card className="shadow-lg">
        <CardBody className="p-0">
          <div className="border-b border-slate-200 px-5 py-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Status chart</div>
          </div>
            <div className="h-[340px] px-3 py-4">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={statusChart}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month_label" tickMargin={10} />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="DRAFT" stackId="invoice-status" fill={statusColors.DRAFT || '#f59e0b'} />
                  <Bar dataKey="SENT" stackId="invoice-status" fill={statusColors.SENT || '#0ea5e9'} />
                  <Bar dataKey="PAID" stackId="invoice-status" fill={statusColors.PAID || '#10b981'} />
                </BarChart>
            </ResponsiveContainer>
          </div>
        </CardBody>
      </Card>

      <Card className="shadow-lg xl:col-span-2">
        <CardBody className="p-0">
          <div className="border-b border-slate-200 px-5 py-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Payment split</div>
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <div className="overflow-x-auto">
              <table className="min-w-full text-xs">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">Bucket</th>
                    <th className="px-4 py-3 text-right font-semibold">Count</th>
                    <th className="px-4 py-3 text-right font-semibold">Net</th>
                  </tr>
                </thead>
                <tbody>
                  {paymentRows.map((row) => (
                    <tr key={row.bucket} className="border-t border-slate-200">
                      <td className="px-4 py-3 font-semibold text-slate-900">{row.bucket === 'PAID' ? 'Paid' : 'Not paid'}</td>
                      <td className="px-4 py-3 text-right font-semibold text-slate-900">{Number(row.count || 0)}</td>
                      <td className="px-4 py-3 text-right font-semibold text-emerald-700">{formatMoney(row.net_amount || 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="h-[300px] px-3 py-4">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={paymentChart} dataKey="count" nameKey="name" innerRadius={80} outerRadius={115} paddingAngle={3}>
                    {paymentChart.map((entry) => (
                      <Cell key={entry.bucket} fill={paymentColors[entry.bucket] || '#64748b'} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

export default MonthlyInvoiceStatusReport;
